import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Box, Typography } from "@mui/material";

import { ActionButton, InputField, CardLayout, Title, ToggleButton, SelectField, CrudTable } from "../v3";
import { AddEditMaterial } from "../library";
import { AccountService, ProjectService, PurchaseService } from "../../services";
import { formatAddPurchasePayload, formatUpdatePurchasePayload } from "../../utils/vendor/AddEditPurchase.util";
import { useHasPermission } from "../../utils";
import {
  CREATE_MATERIAL,
  ENABLE_PURCHASE_BACKDATE_ENTRY,
  ENABLE_VENDOR_ADVANCE_PAYMENT,
  VIEW_ACCOUNT,
  SELECT_OFFICE_ACCOUNT_IN_ENTRIES,
} from "../../constants";
import { PurchaseItemColumns } from "../../pages/vendor/constants";
import { purchaseValidationRules, validateAdvancePayment } from "../../pages/vendor/validation";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddEditPurchaseForm = ({ purchaseId }) => {
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("vendorId");
  const projectId = searchParams.get("projectId");
  const accountType = searchParams.get("accountType");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPaid, setIsPaid] = useState(false);
  const [isDiscount, setIsDiscount] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedProject, setSelectedProject] = useState(
    accountType === "office" ? { id: "office", name: "Company/Office" } : null,
  );
  const [selectedProjectStage, setSelectedProjectStage] = useState(null);
  const [projectStages, setProjectStages] = useState([]);
  const [stageIds, setStageIds] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [rawTotalAmount, setRawTotalAmount] = useState(0);
  const [totalPurchaseAmount, setTotalPurchaseAmount] = useState(0);
  const [purchaseDetails, setPurchaseDetails] = useState({
    vendorId: undefined,
    projectId: accountType === "office" ? "office" : undefined,
    projectStageId: undefined,
    description: undefined,
    receivedById: undefined,
    receivedDate: moment().format("YYYY-MM-DD"),
    discountAmount: 0,
  });
  const [paymentDetails, setPaymentDetails] = useState({
    fromAccountId: undefined,
    paidAmount: undefined,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const enableBackDate = useHasPermission(ENABLE_PURCHASE_BACKDATE_ENTRY);
  const hasCreateMaterial = useHasPermission(CREATE_MATERIAL);
  const enableAdvancePayment = useHasPermission(ENABLE_VENDOR_ADVANCE_PAYMENT);
  const hasSelectOfficeAccount = useHasPermission(SELECT_OFFICE_ACCOUNT_IN_ENTRIES);

  useEffect(() => {
    const rawTotalAmount = purchaseItems.reduce(
      (total = 0, item) =>
        total +
        Number(item?.price || 0) * Number(item?.quantity || 0) +
        (Number(item?.taxPercentage || 0) / 100) * Number(item?.price || 0) * Number(item?.quantity || 0),
      0,
    );
    setRawTotalAmount(rawTotalAmount);

    const discount = Number(purchaseDetails.discountAmount || 0);
    const totalPurchaseAmount = (rawTotalAmount - discount).toFixed(2);
    setTotalPurchaseAmount(totalPurchaseAmount);
  }, [purchaseItems, purchaseDetails.discountAmount]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (purchaseId) fetchData(purchaseId);
    if (!purchaseId && vendorId) fetchVendorDetails(vendorId);
    if (projectId) fetchProject(projectId);
  }, [purchaseId, vendorId, projectId]);

  useEffect(() => {
    setPaymentDetails((prev) => ({ ...prev, paidAmount: totalPurchaseAmount }));
  }, [totalPurchaseAmount]);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
      sortBy: "name",
      sortOrder: "asc",
    }),
    [stageIds],
  );

  const fetchData = async (id) => {
    try {
      // fetch purchase details
      const { data: purchase } = await PurchaseService.getPurchaseById(id);
      if (purchase?.items?.length > 0) {
        // fetch purchase items details for item names
        const itemIds = Array.from(new Set(purchase?.items?.map((item) => item.purchaseItemId)));
        if (itemIds.length > 0) {
          const { data: itemsResponse } = await PurchaseService.getPurchaseItems({
            ids: itemIds.join(","),
            limit: itemIds.length,
          });

          // set purchase items details
          const purchaseItems = purchase?.items?.map((item) => ({
            id: item.purchaseItemId,
            purchaseItemId: item.purchaseItemId,
            name: itemsResponse.rows.find((i) => i.id === item.purchaseItemId)?.name || "Item not found",
            quantity: item.quantity,
            price: item.price,
            taxPercentage: item.taxPercentage,
            unitOfMeasure: item.unitOfMeasure,
          }));
          setPurchaseItems(purchaseItems);
        }
      }

      setSelectedVendor(purchase?.vendor);
      setSelectedProject(purchase?.project || (purchase?.projectId === null ? { id: "office", name: "Company/Office" } : null));

      // fetch project stages
      if (purchase?.projectId) fetchProjectStages(purchase?.projectId);
      // fetch stages
      if (purchase?.projectStageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(purchase?.projectStage?.stageId);
          setSelectedProjectStage(stage);
        } catch (error) {
          toast.error("Failed to fetch stage details");
          console.error("🚀 ~ fetchData ~ error:", error);
        }
      }

      if (Number(purchase?.discountAmount) > 0) {
        setIsDiscount(true);
      }

      // set purchase details
      setPurchaseDetails({
        vendorId: purchase?.vendorId,
        projectId: purchase?.projectId === null || accountType === "office" ? "office" : purchase?.projectId,
        description: purchase?.description,
        receivedById: purchase?.receivedById,
        receivedDate: moment(purchase?.receivedDate).format("YYYY-MM-DD"),
        discountAmount: purchase?.discountAmount,
      });
    } catch (error) {
      console.error("Error fetching purchase details:", error);
    }
  };

  // fetch vendor details and set to vendor select field
  const fetchVendorDetails = async (vendorId) => {
    try {
      const { data: vendor } = await PurchaseService.getVendorById(vendorId);
      setSelectedVendor(vendor);
      setPurchaseDetails((prev) => ({ ...prev, vendorId: vendor.id }));
    } catch (error) {
      console.error("Error fetching vendor details:", error);
    }
  };

  // fetch project details and set to project select field
  const fetchProject = async () => {
    try {
      const { data: project } = await ProjectService.getProjectById(projectId);
      handleProjectChange(project);
    } catch (error) {
      console.error("🚀 ~ fetchProject ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId: any) => {
    try {
      // First call -> get total count
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      // Second call -> fetch all stages
      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = Array.from(new Set(data.rows.map((item: any) => item.stageId)));

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  // fetch accounts and set to account select field
  const fetchAccounts = async () => {
    try {
      const { data: accounts } = await AccountService.getAdminAndUserAccounts({
        hasViewAccount,
        user,
        companyAdminId,
      });
      setSelectedAccount(accounts.rows[0]);
      setPaymentDetails((prev) => ({
        ...prev,
        fromAccountId: accounts.rows[0]?.id,
      }));
    } catch (error) {
      console.error("Error fetching account details:", error);
    }
  };

  // update purchase details state on field change
  const updateDetailsState = (e) => {
    const { name, value } = e.target;

    if (name === "discountAmount") {
      // Allow empty string so user can clear
      if (value === "") {
        setPurchaseDetails((prev) => ({
          ...prev,
          discountAmount: "",
        }));

        setErrors((prev) => {
          const next = { ...prev };
          delete next.discountAmount;
          return next;
        });

        return;
      }

      let discount = Number(value);

      // Prevent negative
      if (discount < 0) discount = 0;

      // Prevent discount greater than total items
      if (discount > rawTotalAmount) {
        setErrors((prev) => ({
          ...prev,
          discountAmount: "Discount cannot exceed total amount",
        }));
        return;
      } else {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.discountAmount;
          return next;
        });
      }

      setPurchaseDetails((prev) => ({
        ...prev,
        discountAmount: discount,
      }));

      return;
    }

    setPurchaseDetails((prev) => ({ ...prev, [name]: value }));
  };

  // set vendor to vendor select field and vendorId to purchase details
  const handleVendorChange = (selected) => {
    setSelectedVendor(selected);
    setPurchaseDetails((prev) => ({ ...prev, vendorId: selected?.id || null }));
  };

  // set project to project select field and projectId to purchase details
  const handleProjectChange = (selected) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setPurchaseDetails((prev) => ({ ...prev, projectId: selected?.id || null, projectStageId: null }));
    if (selected?.id && selected?.id !== "office") fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected) => {
    const projectStage = projectStages.find((item) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setPurchaseDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handlePurchaseItemChange = (selectedItems = []) => {
    const validItems = selectedItems.filter((item) => item && item.id && !item.isAddButton);

    setPurchaseItems((prevItems) => {
      const prevMap = new Map(prevItems.map((item) => [item.purchaseItemId, item]));

      return validItems.map((item) => {
        const existing = prevMap.get(item.id);

        if (existing) return existing;

        return {
          id: item.id,
          purchaseItemId: item.id,
          name: item.name,
          quantity: 1,
          unitOfMeasure: item.unitOfMeasure,
          price: Number(item.defaultPrice) ?? 0,
          taxPercentage: Number(item.taxPercentage) ?? 0,
        };
      });
    });
  };

  const handleAccountChange = (selected) => {
    setSelectedAccount(selected);
    setPaymentDetails((prev) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const handleDiscountClick = (e) => {
    const checked = e.target.checked;
    setIsDiscount(checked);

    if (!checked) {
      // Reset discount when disabled
      setPurchaseDetails((prev) => ({
        ...prev,
        discountAmount: 0,
      }));

      setErrors((prev) => {
        const next = { ...prev };
        delete next.discountAmount;
        return next;
      });
    }
  };

  const handlePaidClick = (e) => {
    setIsPaid(e.target.checked);
    setPaymentDetails({ ...paymentDetails, paidAmount: totalPurchaseAmount });
  };

  const handlePaymentDetailsChange = (event) => {
    const { name, value } = event.target;
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "paidAmount") {
      let amountError;

      if (value !== "" && Number(value) < 1) {
        amountError = "Amount must be greater than 0";
      } else {
        amountError = validateAdvancePayment({
          amount: value,
          balance: Number(formatBalance(selectedVendor?.account?.balance)) + Number(totalPurchaseAmount),
          enableAdvancePayment,
        });
      }

      setErrors((prev) => {
        const nextErrors = { ...prev };

        if (amountError) nextErrors.paidAmount = amountError;
        else delete nextErrors.paidAmount;

        return nextErrors;
      });
    }
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleClearForm = () => {
    setErrors({});
    setPurchaseDetails({
      vendorId: vendorId ?? undefined,
      projectId: accountType === "office" ? "office" : undefined,
      projectStageId: undefined,
      description: undefined,
      receivedById: user?.id,
      receivedDate: moment().format("YYYY-MM-DD"),
    });
    setPurchaseItems([]);
    setPaymentDetails({ ...paymentDetails, paidAmount: undefined });
    setSelectedProject(accountType === "office" ? { id: "office", name: "Company/Office" } : null);
    setSelectedProjectStage(null);
    setSelectedVendor(null);
    setIsPaid(false);
    setIsDiscount(false);
  };

  const handleMaterialSuccess = async (material) => {
    setOpenModal(false);

    if (!material?.id) return;

    setPurchaseItems((prev) => {
      if (prev.some((item) => item.purchaseItemId === material.id)) {
        return prev;
      }

      return [
        ...prev,
        {
          id: material.id,
          purchaseItemId: material.id,
          name: material.name,
          quantity: 1,
          unitOfMeasure: material.unitOfMeasure,
          price: material.defaultPrice ?? 0,
          taxPercentage: material.taxPercentage ?? 0,
        },
      ];
    });
  };

  const formatBalance = (value) => {
    const num = Number(value);

    if (isNaN(num) || num === 0) return 0;
    return (-num).toFixed(2);
  };

  const getAmountLabel = ({ enableAdvancePayment, balance, amount = 0 }) => {
    if (!enableAdvancePayment && balance !== null) {
      const totalAmount = Number(amount) + Number(balance);
      return `Paid Amount (Balance: ${Number(totalAmount).toLocaleString()})`;
    }

    return "Paid Amount";
  };

  const handleSavePurchase = async () => {
    if (loading) return;
    setErrors({});
    const validationErrors = purchaseValidationRules({
      purchaseDetails,
      purchaseItems,
      paymentDetails,
      isPaid: !!isPaid,
      options: {
        balance: Number(formatBalance(selectedVendor?.account?.balance)) + Number(totalPurchaseAmount),
        enableAdvancePayment,
      },
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      setLoading(true);

      if (purchaseId) {
        const updatePurchasePayload = formatUpdatePurchasePayload({
          purchaseDetails: {
            ...purchaseDetails,
            projectId: purchaseDetails.projectId === "office" ? null : purchaseDetails.projectId,
            discountAmount: Number(purchaseDetails.discountAmount || 0),
          },
          purchaseItems,
        });
        await PurchaseService.updatePurchase(purchaseId, updatePurchasePayload);
      } else {
        const createPurchasePayload = formatAddPurchasePayload({
          purchaseDetails: {
            ...purchaseDetails,
            projectId: purchaseDetails.projectId === "office" ? null : purchaseDetails.projectId,
            discountAmount: Number(purchaseDetails.discountAmount || 0),
          },
          paymentDetails: isPaid ? paymentDetails : {},
          purchaseItems,
          isPaid,
        });
        await PurchaseService.createPurchase({
          ...createPurchasePayload,
          receivedById: user?.id,
          ...(accountType === "office" && { projectId: null }),
        });
      }
      toast.success(`Purchase ${purchaseId ? "updated" : "created"} successfully`);
      handleClearForm();
      if (vendorId || purchaseId) {
        // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
        if (!purchaseId) markTableReset();
        navigate(-1);
      } else if (projectId) navigate(`/projects/${projectId}?tab=purchases`);
    } catch (error) {
      console.error(`🚀 ~ handleSavePurchase ~ error:`, error);
      toast.error(`Failed to ${purchaseId ? "update" : "create"} purchase`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardLayout>
        <Box display="flex" gap={2}>
          <SelectField
            disabled={!!vendorId || !!purchaseId}
            label="Vendor"
            value={selectedVendor}
            getData={PurchaseService.getVendors}
            getDataStaticFilters={{ type: "VENDOR", active: true, sortBy: "name", sortOrder: "asc", includeBalance: true }}
            required
            error={!!errors.vendorId}
            helperText={errors.vendorId}
            onChange={handleVendorChange}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Project"
            value={selectedProject}
            defaultOption={hasSelectOfficeAccount ? { id: "office", name: "Company/Office" } : undefined} // TODO: replace this with a proper option for company/office project when available in the API
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
            disabled={!!projectId || accountType === "office"}
            required
            error={!!errors.projectId}
            helperText={errors.projectId}
            onChange={handleProjectChange}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Stage"
            value={selectedProjectStage}
            getData={ProjectService.getStages}
            getDataStaticFilters={stageFilter}
            disabled={stageIds.length === 0 || purchaseDetails.projectId === "office"}
            error={!!errors.projectStageId}
            helperText={errors.projectStageId}
            onChange={handleProjectStageChange}
            style={{ width: "100%" }}
          />
          <InputField
            type="date"
            label="Date"
            name="receivedDate"
            value={purchaseDetails.receivedDate}
            onChange={updateDetailsState}
            error={!!errors.receivedDate}
            helperText={errors.receivedDate}
            style={{ width: "100%" }}
            inputSlotProps={{
              htmlInput: {
                min: enableBackDate ? undefined : moment().format("YYYY-MM-DD"),
                onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
              },
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <InputField
            label="Description"
            name="description"
            value={purchaseDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            autoComplete="on"
          />
        </Box>
      </CardLayout>

      <CardLayout>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Add Item
        </Typography>

        <Box display="flex" mt={2}>
          <SelectField
            label="Item"
            placeholder="Select Item"
            value={purchaseItems}
            getData={PurchaseService.getPurchaseItems}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            limitTags={5}
            error={!!errors.purchaseItemId}
            helperText={errors.purchaseItemId}
            onChange={handlePurchaseItemChange}
            addButtonText="Add Item"
            showAddButton={hasCreateMaterial}
            onAddClick={() => setOpenModal(true)}
            style={{ width: "100%" }}
          />
        </Box>

        <Typography variant="caption" sx={{ color: "error.main", fontWeight: 500 }}>
          {errors.purchaseItems}
        </Typography>
        <CrudTable
          loading={loading}
          rows={purchaseItems}
          setRows={setPurchaseItems}
          columns={PurchaseItemColumns()}
          hidePagination
        />

        <Box display="flex" justifyContent="flex-end" mt={2} mb={2}>
          <Title textVariant="p" title={`Sub Total: ${Number(rawTotalAmount).toLocaleString()}`} style={{ fontWeight: "bold" }} />
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={2}>
          <ToggleButton
            label="Add Discount"
            disabled={!purchaseItems.length}
            checked={isDiscount}
            onToggleChange={handleDiscountClick}
            style={{ ml: 0 }}
          />

          {isDiscount && (
            <Box sx={{ width: 250 }}>
              <InputField
                type="number"
                label="Discount Amount"
                name="discountAmount"
                value={purchaseDetails.discountAmount}
                onChange={updateDetailsState}
                error={!!errors.discountAmount}
                helperText={errors.discountAmount}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
          {!purchaseId ? (
            <ToggleButton label="Paid" checked={isPaid} onToggleChange={handlePaidClick} style={{ ml: 0 }} />
          ) : (
            <Box />
          )}
          <Title
            textVariant="p"
            title={`Total: ${Number(totalPurchaseAmount).toLocaleString()}`}
            style={{ fontWeight: "bold" }}
          />
        </Box>

        {isPaid && (
          <Box display="flex" gap={2}>
            <SelectField
              label="Account"
              value={selectedAccount}
              getData={AccountService.getAdminAndUserAccounts}
              getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
              required
              error={!!errors.fromAccountId}
              helperText={errors.fromAccountId}
              onChange={handleAccountChange}
              style={{ width: "100%" }}
            />

            <InputField
              required
              label={getAmountLabel({
                enableAdvancePayment,
                balance: formatBalance(selectedVendor?.account?.balance),
                amount: totalPurchaseAmount,
              })}
              name="paidAmount"
              type="number"
              value={paymentDetails.paidAmount}
              onChange={handlePaymentDetailsChange}
              error={!!errors.paidAmount}
              helperText={errors.paidAmount}
              style={{ width: "100%" }}
            />
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
          <ActionButton buttonText="save" color="primary" onButtonClick={handleSavePurchase} loading={loading} />
        </Box>
      </CardLayout>

      {/* Add Item Modal */}
      <AddEditMaterial open={openModal} onClose={() => setOpenModal(false)} onSuccess={handleMaterialSuccess} />
    </>
  );
};
